import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext.jsx';
import { PERMISSIONS } from '../../../lib/permissions';
import supabase from '../../../lib/supabaseClient.js';
import { Label } from "../../ui/label";
import Button from '../../ui/button';
import {
  Clock,
  Settings,
  AlertCircle,
  TrendingUp,
  Bell,
  Brain
} from 'lucide-react';

const DELAY_OPTIONS = ['1', '3', '7', '14', '30', '60', '90', '180'];
const METHOD_OPTIONS = ['Email', 'SMS', 'Dashboard Only', 'All'];

const AutomationRules = () => {
  const { user, hasPermission } = useAuth();
  
  const canEditAISettings = hasPermission(PERMISSIONS.VIEW_EDIT_AI_SETTINGS);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [minScore, setMinScore] = useState('75');
  const [followupDelay, setFollowupDelay] = useState('3');
  const [escalationMethod, setEscalationMethod] = useState('All');

  // Calculate thresholds based on minScore
  const hotThreshold = parseInt(minScore);
  const warmThreshold = Math.round(hotThreshold * 0.71);
  const coldThreshold = Math.round(hotThreshold * 0.29);

  const getApiBaseUrl = () => {
    if (process.env.REACT_APP_API_URL) {
      return process.env.REACT_APP_API_URL;
    }
    if (process.env.NODE_ENV === 'development') {
      return 'http://localhost:3001';
    }
    return 'https://api.getsurfox.com/api';
  };

  const callAPI = async (endpoint, options = {}) => {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session?.access_token) {
      throw new Error('Authentication required');
    }

    const apiBaseUrl = getApiBaseUrl();
    const fullUrl = `${apiBaseUrl}${endpoint}`;

    const response = await fetch(fullUrl, {
      ...options,
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch (e) {
        errorData = { error: `Server error (${response.status})` };
      }
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    const responseText = await response.text();
    if (!responseText) return {};
    return JSON.parse(responseText);
  };

  // Load settings on mount
  useEffect(() => {
    const fetchSettings = async () => {
      if (!user?.tenant_id) {
        setLoading(false);
        return;
      }

      try {
        const settings = await callAPI(`/settings?tenant_id=${user.tenant_id}`);
        setMinScore(settings['ai_min_escalation_score']?.value || '75');
        setFollowupDelay(settings['ai_cold_followup_delay_days']?.value || '3');
        setEscalationMethod(settings['ai_escalation_method']?.value || 'All');
      } catch (err) {
        console.error('Error loading AI settings:', err.message);
        setError(`Failed to load settings: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, [user?.tenant_id]);

  const handleSave = async () => {
    if (!user?.tenant_id) {
      setError('User or tenant information is missing.');
      return;
    }
    
    if (!canEditAISettings) {
      setError('You don\'t have permission to save AI settings.');
      return;
    }

    setSaving(true);
    setSuccess('');
    setError('');

    try {
      await callAPI('/settings', {
        method: 'PUT',
        body: JSON.stringify({
          settings: {
            ai_min_escalation_score: { value: minScore },
            ai_cold_followup_delay_days: { value: followupDelay },
            ai_escalation_method: { value: escalationMethod }
          },
          tenant_id: user.tenant_id
        })
      });

      setSuccess('Settings saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(`Save failed: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center space-x-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <span className="text-red-800">{error}</span>
        </div>
      )}
      
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center space-x-3">
          <AlertCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
          <span className="text-green-800 font-medium">{success}</span>
        </div>
      )}

      {!canEditAISettings && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex items-center space-x-3">
          <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0" />
          <span className="text-yellow-800">
            You have read-only access to AI settings. Admin permissions required to make changes.
          </span>
        </div>
      )}

      {/* Escalation Scoring */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
            <TrendingUp className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Escalation Scoring</h3>
            <p className="text-gray-600 text-sm">Configure when leads should be escalated to your sales team</p>
          </div>
        </div>

        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Minimum Score to Escalate</span>
            <div className="flex items-center gap-3">
              <div 
                className="px-3 py-1 rounded-full text-sm font-medium"
                style={{ 
                  backgroundColor: parseInt(minScore) >= 85 ? '#fef2f2' : 
                                 parseInt(minScore) >= 65 ? '#fff7ed' : 
                                 parseInt(minScore) >= 45 ? '#fefce8' : '#eff6ff',
                  color: parseInt(minScore) >= 85 ? '#ef4444' : 
                         parseInt(minScore) >= 65 ? '#f97316' : 
                         parseInt(minScore) >= 45 ? '#eab308' : '#3b82f6'
                }}
              >
                {parseInt(minScore) >= hotThreshold ? '🔥 Hot Lead' : 
                  parseInt(minScore) >= warmThreshold ? '🌶️ Warm Lead' : 
                  parseInt(minScore) >= coldThreshold ? '☀️ Responding' : '❄️ Cold Lead'}
              </div>
              <div className="text-2xl font-bold text-gray-900 min-w-[60px] text-right">
                {minScore}
              </div>
            </div>
          </div>

          <div className="relative">
            <input
              type="range"
              min="1"
              max="100"
              value={minScore}
              onChange={(e) => setMinScore(e.target.value)}
              disabled={!canEditAISettings}
              className={`w-full h-2 bg-gray-200 rounded-lg appearance-none automation-slider ${!canEditAISettings ? 'cursor-not-allowed' : 'cursor-pointer'}`}
              style={{
                background: `linear-gradient(to right, 
                  #3b82f6 0%, 
                  #3b82f6 ${coldThreshold}%, 
                  #eab308 ${coldThreshold}%, 
                  #eab308 ${warmThreshold}%, 
                  #f97316 ${warmThreshold}%, 
                  #f97316 ${hotThreshold}%, 
                  #ef4444 ${hotThreshold}%, 
                  #ef4444 100%)`
              }}
            />
            
            <div className="flex justify-between mt-2 px-1">
              <div className="bg-white/60 rounded-lg p-3">
                <div className="font-semibold text-red-600">🔥 {hotThreshold}-100</div>
                <div className="text-gray-600 text-sm">Hot Lead</div>
              </div>
              <div className="bg-white/60 rounded-lg p-3">
                <div className="font-semibold text-orange-600">🌶️ {warmThreshold}-{hotThreshold - 1}</div>
                <div className="text-gray-600 text-sm">Warm Lead</div>
              </div>
              <div className="bg-white/60 rounded-lg p-3">
                <div className="font-semibold text-yellow-600">☀️ {coldThreshold}-{warmThreshold - 1}</div>
                <div className="text-gray-600 text-sm">Responding</div>
              </div>
              <div className="bg-white/60 rounded-lg p-3">
                <div className="font-semibold text-blue-600">❄️ 0-{coldThreshold - 1}</div>
                <div className="text-gray-600 text-sm">Cold Lead</div>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-600">
              Leads scoring <span className="font-semibold text-gray-900">{minScore} or higher</span> will be automatically escalated to your sales team.
            </p>
            <div className="mt-2 text-xs text-gray-500">
              💡 Most SaaS companies set this between 70-80 for optimal balance
            </div>
          </div>
        </div>
      </div>

      {/* Follow-up Timing */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
            <Clock className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Follow-up Timing</h3>
            <p className="text-gray-600 text-sm">Control how long the AI waits before following up with cold leads</p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <Label htmlFor="followupDelay" className="flex items-center space-x-2 mb-2">
              <Clock className="w-4 h-4 text-gray-500" />
              <span>Cold Follow-Up Delay</span>
            </Label>
            <div className="max-w-xs">
              <select
                id="followupDelay"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                value={followupDelay}
                onChange={(e) => setFollowupDelay(e.target.value)}
                disabled={!canEditAISettings}
              >
                {DELAY_OPTIONS.map((d) => (
                  <option key={d} value={d}>
                    {d === '180' ? '6 months' : d === '1' ? '1 day' : `${d} days`}
                  </option>
                ))}
              </select>
            </div>
            <p className="text-sm text-gray-500 mt-2">
              Time to wait before sending follow-up messages to unresponsive leads.
            </p>
          </div>
        </div>
      </div>

      {/* Escalation Notifications */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center">
            <Bell className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Escalation Notifications</h3>
            <p className="text-gray-600 text-sm">Choose how you want to be notified about hot leads</p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <Label htmlFor="escalationMethod" className="flex items-center space-x-2 mb-2">
              <Bell className="w-4 h-4 text-gray-500" />
              <span>Notification Method</span>
            </Label>
            <div className="max-w-xs">
              <select
                id="escalationMethod"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                value={escalationMethod}
                onChange={(e) => setEscalationMethod(e.target.value)}
                disabled={!canEditAISettings}
              >
                {METHOD_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
            <p className="text-sm text-gray-500 mt-2">
              How you'll be alerted when leads meet your escalation criteria.
            </p>
          </div>
        </div>
      </div>

      {/* Current Configuration Summary */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-orange-50 rounded-lg flex items-center justify-center">
            <Settings className="w-5 h-5 text-orange-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Current Configuration</h3>
            <p className="text-gray-600 text-sm">Summary of your AI automation settings</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-gray-900">{minScore}/100</div>
            <div className="text-sm text-gray-600">Escalation Score</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-gray-900">
              {followupDelay === '180' ? '6mo' : followupDelay === '1' ? '1d' : `${followupDelay}d`}
            </div>
            <div className="text-sm text-gray-600">Follow-up Delay</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-lg font-bold text-gray-900">{escalationMethod}</div>
            <div className="text-sm text-gray-600">Alert Method</div>
          </div>
        </div>
      </div>

      {/* Score Reference Guide */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl border border-blue-200 p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
            <Brain className="w-4 h-4 text-blue-600" />
          </div>
          <h4 className="text-sm font-semibold text-gray-900">Score Reference Guide</h4>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div className="bg-white/60 rounded-lg p-3">
            <div className="font-semibold text-red-600">🔥 85-100</div>
            <div className="text-gray-600">Hot Lead</div>
          </div>
          <div className="bg-white/60 rounded-lg p-3">
            <div className="font-semibold text-orange-600">🌶️ 65-84</div>
            <div className="text-gray-600">Warm Lead</div>
          </div>
          <div className="bg-white/60 rounded-lg p-3">
            <div className="font-semibold text-yellow-600">☀️ 45-64</div>
            <div className="text-gray-600">Responding</div>
          </div>
          <div className="bg-white/60 rounded-lg p-3">
            <div className="font-semibold text-blue-600">❄️ 0-44</div>
            <div className="text-gray-600">Cold Lead</div>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving || !canEditAISettings}
          className="px-6 py-2"
        >
          {saving ? (
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              <span>Saving...</span>
            </div>
          ) : (
            "Save Settings"
          )}
        </Button>
      </div>

      <style jsx>{`
        .automation-slider::-webkit-slider-thumb {
          appearance: none;
          height: 20px;
          width: 20px;
          border-radius: 50%;
          background: #ffffff;
          border: 2px solid #3b82f6;
          cursor: pointer;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        
        .automation-slider::-moz-range-thumb {
          height: 20px;
          width: 20px;
          border-radius: 50%;
          background: #ffffff;
          border: 2px solid #3b82f6;
          cursor: pointer;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        
        .automation-slider:disabled::-webkit-slider-thumb {
          background: #e5e7eb;
          border-color: #9ca3af;
          cursor: not-allowed;
        }
        
        .automation-slider:disabled::-moz-range-thumb {
          background: #e5e7eb;
          border-color: #9ca3af;
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
};

export default AutomationRules;